import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, Menu, MessageCircle, Phone, Star, X } from 'lucide-react';
import { CLINIC_INFO } from '../../data/mock';

const HEADER_HEIGHT_PX = 64;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navigation = useMemo(
    () => [
      { name: 'Inicio', href: '/' },
      { name: 'Servicios', href: '/servicios' },
      { name: 'Equipo', href: '/equipo' },
      { name: 'Testimonios', href: '/testimonios' },
    ],
    []
  );

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const whatsappMessage = encodeURIComponent(
    `Hola, quiero agendar una cita en Áurea Dental. Mi nombre es ___, busco ___, mi disponibilidad es ___.`
  );

  const whatsappHref = `https://wa.me/${CLINIC_INFO.whatsapp}?text=${whatsappMessage}`;
  const calendlyHref = CLINIC_INFO.calendlyUrl;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'
        }`}
        style={{ height: `${HEADER_HEIGHT_PX}px` }}
      >
        <nav className="container h-full flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[var(--color-primary)] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">Á</span>
            </div>
            <div className="flex flex-col">
                <span className="text-lg font-semibold text-[var(--text-primary)] leading-tight">
                  Áurea Dental
                </span>
              <span className="hidden sm:block text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
                  Integral
                </span>
              </div>
          </Link>

          <ul className="hidden lg:flex items-center gap-2">
            {navigation.map((item) => {
              const active = isActive(item.href);
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    aria-current={active ? 'page' : undefined}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 ${
                      active
                        ? 'bg-[var(--bg-secondary)] text-[var(--text-primary)]'
                        : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
                    }`}
                  >
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="hidden lg:flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-secondary)] rounded-full">
              <Star size={14} className="fill-[var(--color-accent)] text-[var(--color-accent)]" />
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {CLINIC_INFO.googleRating}
              </span>
              <span className="text-xs text-[var(--text-muted)]">Google</span>
            </div>

            <a
              href={`tel:${CLINIC_INFO.phone}`}
              className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 rounded-md"
              aria-label={`Llamar al ${CLINIC_INFO.phone}`}
            >
              <Phone size={16} />
            </a>

            <a
              href={calendlyHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              <Calendar size={16} />
              Agendar cita
            </a>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-whatsapp"
            >
              <MessageCircle size={16} />
              WhatsApp
            </a>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <a
              href={calendlyHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-sm"
              >
                <Calendar size={16} />
                <span className="hidden sm:inline">Agendar</span>
            </a>
            <button
              type="button"
              className="p-2 text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 rounded-md"
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
                aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>
      </header>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menú"
        className={`fixed left-0 right-0 z-40 bg-white border-b border-[var(--border-light)] lg:hidden transition-all duration-300 ${
          mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
        style={{ top: `${HEADER_HEIGHT_PX}px` }}
        >
          <div className="container py-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <a
                href={calendlyHref}
                target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary w-full justify-center"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Calendar size={18} />
              Agendar cita
            </a>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-whatsapp w-full justify-center"
              onClick={() => setMobileMenuOpen(false)}
            >
              <MessageCircle size={18} />
              WhatsApp
            </a>
          </div>

          <a
            href={`tel:${CLINIC_INFO.phone}`}
            className="btn btn-secondary w-full justify-center mb-6"
            onClick={() => setMobileMenuOpen(false)}
          >
            <Phone size={18} />
            Llamar
          </a>

          <ul className="flex flex-col gap-2">
            {navigation.map((item) => {
              const active = isActive(item.href);
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    aria-current={active ? 'page' : undefined}
                    className={`block py-3 px-4 rounded-lg text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 ${
                      active
                        ? 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center gap-2 mt-6 pt-6 border-t border-[var(--border-light)]">
            <div className="flex items-center gap-1">
              <Star size={16} className="fill-[var(--color-accent)] text-[var(--color-accent)]" />
              <span className="font-semibold">{CLINIC_INFO.googleRating}/5</span>
            </div>
            <span className="text-sm text-[var(--text-muted)]">
              {CLINIC_INFO.totalReviews} reseñas en Google
            </span>
          </div>
        </div>
      </div>

      <div style={{ height: `${HEADER_HEIGHT_PX}px` }} />
    </>
  );
};

export default Header;
